import { memo, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { BookmarkListItem, ViewMode, DisplayLanguage } from 'markcat-domain';
import { bookmarkImagePath, formatExactDate, formatRelativeTime, formatUrl, joinCategoryPath, splitCategoryPath, isPendingStatus, isFailedStatus } from 'markcat-domain';
import { BookmarkBadges, computeBadges } from './BookmarkBadges';
import type { LanguageOption } from './LanguageSwitcher';
import { TagIcon, TranslateIcon } from './icons';
import { useBookmarkAsset } from './useBookmarkAsset';
import styles from './BookmarkCard.module.css';

export type CategoryDisplayMode = 'last' | 'full';
export type { DisplayLanguage };
export type HeroImageSource = 'screenshot' | 'og_image';

/** One panel of the tiles carousel. `hero` shows the best image per
 *  `heroImageSource` (favicon fallback); the rest are explicit. The carousel is
 *  image-only — the summary lives on the card surface and in the preview drawer. */
type TileView = 'hero' | 'og' | 'screenshot';
const NO_VIEWS: TileView[] = [];

/** Asset paths that have finished decoding once this session. A card whose
 *  path is known mounts with the crossfade pre-completed, so virtualizer
 *  remounts (tiles row keys shift on add/delete) don't replay a full-grid fade. */
const LOADED_ASSET_PATHS_MAX = 150;
const loadedAssetPaths = new Set<string>();

function rememberLoadedAsset(path: string): void {
  loadedAssetPaths.delete(path);
  loadedAssetPaths.add(path);
  if (loadedAssetPaths.size <= LOADED_ASSET_PATHS_MAX) return;
  const oldest = loadedAssetPaths.values().next().value;
  if (oldest !== undefined) loadedAssetPaths.delete(oldest);
}

/** Tile badge variant → opaque overlay fill. The list-view soft tints are
 *  translucent and lose AA contrast over a hero image, so the over-image
 *  overlay uses solid, theme-independent fills (each ≥4.5:1 with white). */
const TILE_BADGE_VARIANT: Record<string, string> = {
  error: styles.tbError,
  warning: styles.tbWarning,
  info: styles.tbInfo,
  success: styles.tbSuccess,
  neutral: styles.tbNeutral,
};

interface BookmarkCardProps {
  bookmark: BookmarkListItem;
  viewMode: ViewMode;
  selected?: boolean;
  /** One-shot arrival flash for the bookmark the user just added. */
  justAdded?: boolean;
  selectionMode?: boolean;
  showExactDates?: boolean;
  showMetaButton?: boolean;
  categoryDisplayMode?: CategoryDisplayMode;
  displayLanguage?: DisplayLanguage;
  /** Toolbar language options — supplies the real language name for the
   *  fallback chip (instead of slot codes like "L2"). */
  languageOptions?: LanguageOption[];
  heroImageSource?: HeroImageSource;
  apiBaseUrl?: string;
  loadAssetUrl?: (path: string) => Promise<string>;
  /** Tiles only: when false, render hero-image-only (no dots/arrows/swipe/
   *  alternate panels). */
  enableCarousel?: boolean;
  dragIds?: string[];
  index?: number;
  selectionAnchorIndex?: number | null;
  /** Tiles only: active carousel panel, owned by BookmarkTiles (keyed by id so
   *  it survives the virtualizer remounting cards on scroll). The card passes
   *  its own id so the setter can stay reference-stable across cards. */
  activeView?: number;
  onSetActiveView?: (id: string, index: number) => void;
  onToggleSelect?: (id: string) => void;
  onClick?: (bookmark: BookmarkListItem, event?: React.MouseEvent) => void;
  /** Explicit "open the URL" action — middle-click on the card root. Plain
   *  click goes to onClick (preview drawer); this is the URL escape hatch. */
  onOpenUrl?: (bookmark: BookmarkListItem) => void;
  onContextMenu?: (e: React.MouseEvent, bookmark: BookmarkListItem) => void;
  onSelectCategory?: (path: string) => void;
  onShowMeta?: (bookmark: BookmarkListItem) => void;
  onToggleFavourite?: (id: string) => void;
  onEnterSelection?: (id: string) => void;
  onSetSelectionAnchor?: (index: number) => void;
  onRangeSelect?: (startIndex: number, endIndex: number) => void;
  searchQuery?: string;
}

function BookmarkCardImpl({
  bookmark,
  viewMode,
  selected = false,
  justAdded = false,
  selectionMode = false,
  showExactDates = false,
  showMetaButton = false,
  categoryDisplayMode = 'last',
  displayLanguage = 'original',
  languageOptions,
  heroImageSource = 'screenshot',
  apiBaseUrl = '',
  loadAssetUrl,
  enableCarousel = false,
  dragIds,
  index,
  selectionAnchorIndex = null,
  activeView,
  onSetActiveView,
  onToggleSelect,
  onClick,
  onOpenUrl,
  onContextMenu,
  onSelectCategory,
  onShowMeta,
  onToggleFavourite,
  onEnterSelection,
  onSetSelectionAnchor,
  onRangeSelect,
  searchQuery,
}: BookmarkCardProps) {
  const [faviconError, setFaviconError] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);

  // Reset favicon error when bookmark changes (virtualized list reuse)
  useEffect(() => { setFaviconError(false); }, [bookmark.id]);

  // Clean up long press timer on unmount
  useEffect(() => {
    return () => { if (longPressTimer.current) clearTimeout(longPressTimer.current); };
  }, []);

  // Display title/summary are resolved server-side for the tenant's display
  // language and served on the slim list item. The fallback chip still keys off
  // the active language (a non-original language whose summary slot was empty).
  const displayTitle = bookmark.display_title ?? '';
  const displaySummary = bookmark.display_summary ?? '';
  const summaryIsFallback = bookmark.summary_is_fallback ?? false;
  const langInfo = displaySummary && displayLanguage !== 'original'
    ? { label: languageLabel(displayLanguage, languageOptions), isFallback: summaryIsFallback }
    : null;
  const faviconPath = bookmark.has_favicon === 1
    ? bookmarkImagePath(bookmark.id, 'favicon', bookmark.favicon_version)
    : null;

  // Tiles carousel: the set of panels that actually have content, and the
  // active one (clamped — a stored index can fall out of range when an image
  // finishes processing and changes the set). With the carousel disabled the
  // tile shows only the hero, so the set collapses to a single panel.
  const isTiles = viewMode === 'tiles';
  const tileViews = useMemo(
    () => (isTiles && enableCarousel ? buildTileViews(bookmark, heroImageSource) : NO_VIEWS),
    [isTiles, enableCarousel, bookmark.has_og_image, bookmark.has_screenshot, heroImageSource],
  );
  const activeIndex = tileViews.length
    ? Math.min(Math.max(activeView ?? 0, 0), tileViews.length - 1)
    : 0;
  const activeKind = tileViews[activeIndex];
  // One image fetch per card: the active carousel panel's asset (tiles with the
  // carousel on), the hero pick (tiles with it off), or a single best-image
  // thumbnail (list / timeline row).
  const previewKind = isTiles
    ? (activeKind ? assetKindForView(activeKind, bookmark, heroImageSource) : pickPreviewKind(bookmark, heroImageSource))
    : pickPreviewKind(bookmark, heroImageSource);
  const previewPath = previewKind === 'screenshot'
    ? bookmarkImagePath(bookmark.id, 'screenshot', bookmark.screenshot_version)
    : previewKind === 'og_image'
      ? bookmarkImagePath(bookmark.id, 'og-image', bookmark.og_image_version)
      : null;

  const { src: faviconSrc } = useBookmarkAsset(faviconPath, apiBaseUrl, loadAssetUrl);
  const { src: previewSrc, failed: previewFailed } = useBookmarkAsset(previewPath, apiBaseUrl, loadAssetUrl);
  const previewCached = previewPath != null && loadedAssetPaths.has(previewPath);

  useEffect(() => {
    if (previewCached && previewPath) rememberLoadedAsset(previewPath);
  }, [previewCached, previewPath]);

  // The asset can arrive late, swap kind, or reload — key the reset on the path
  // so stale error/loaded state never sticks.
  useEffect(() => { setPreviewError(false); setImgLoaded(false); }, [previewPath]);

  const goView = useCallback((next: number) => {
    if (tileViews.length < 2) return;
    const n = tileViews.length;
    const wrapped = ((next % n) + n) % n; // wrap both ways: last → first, first → last
    if (wrapped !== activeIndex) onSetActiveView?.(bookmark.id, wrapped);
  }, [onSetActiveView, bookmark.id, tileViews.length, activeIndex]);

  const handleClick = useCallback((e?: React.MouseEvent) => {
    // Swallow the click that ends a long-press or a view swipe.
    if (longPressTriggered.current || swiped.current) {
      longPressTriggered.current = false;
      swiped.current = false;
      e?.preventDefault();
      return;
    }

    if (
      e &&
      selectionMode &&
      e.shiftKey &&
      index != null &&
      selectionAnchorIndex != null &&
      onRangeSelect
    ) {
      e.preventDefault();
      onRangeSelect(selectionAnchorIndex, index);
      return;
    }

    // Ctrl/Cmd+Click: enter selection mode + toggle this item.
    if (e && (e.ctrlKey || e.metaKey) && onEnterSelection) {
      e.preventDefault();
      onEnterSelection(bookmark.id);
      if (index != null) onSetSelectionAnchor?.(index);
      return;
    }
    if (selectionMode && onToggleSelect) {
      onToggleSelect(bookmark.id);
      if (index != null) onSetSelectionAnchor?.(index);
    } else if (onClick) {
      onClick(bookmark, e);
      if (index != null) onSetSelectionAnchor?.(index);
    }
  }, [
    bookmark,
    index,
    onClick,
    onEnterSelection,
    onRangeSelect,
    onSetSelectionAnchor,
    onToggleSelect,
    selectionAnchorIndex,
    selectionMode,
  ]);

  // Middle-click opens the URL (the explicit escape hatch from the preview
  // drawer). Ignore aux clicks that originate on a nested control.
  const handleAuxClick = useCallback((e: React.MouseEvent) => {
    if (e.button !== 1 || !onOpenUrl) return;
    const target = e.target instanceof HTMLElement ? e.target : null;
    if (target?.closest('input, button, [role="button"]')) return;
    e.preventDefault();
    onOpenUrl(bookmark);
  }, [bookmark, onOpenUrl]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!onEnterSelection || selectionMode || event.button !== 0) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest('input, button, [role="button"]')) return;
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      longPressTriggered.current = true;
      onEnterSelection(bookmark.id);
      if (index != null) onSetSelectionAnchor?.(index);
    }, 500);
  }, [bookmark.id, index, onEnterSelection, onSetSelectionAnchor, selectionMode]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Touch swipe on the media frame: change panel, never on a tap (40px / mostly
  // horizontal). A swipe also cancels the pending long-press and suppresses the
  // card-open click. Touch-only so it can't collide with desktop HTML5 drag.
  const onMediaPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') swipeStart.current = { x: e.clientX, y: e.clientY };
  }, []);
  const onMediaPointerMove = useCallback((e: React.PointerEvent) => {
    const s = swipeStart.current;
    if (!s) return;
    if (Math.abs(e.clientX - s.x) > 10 || Math.abs(e.clientY - s.y) > 10) cancelLongPress();
  }, [cancelLongPress]);
  const onMediaPointerUp = useCallback((e: React.PointerEvent) => {
    const s = swipeStart.current;
    swipeStart.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      swiped.current = true;
      goView(activeIndex + (dx < 0 ? 1 : -1));
    }
  }, [goView, activeIndex]);

  // Date only renders in the list/timeline trailing column — skip the format
  // work entirely for tiles.
  const displayDate = isTiles
    ? null
    : showExactDates
      ? formatExactDate(bookmark.date)
      : formatRelativeTime(bookmark.date);

  // Card-level processing state — tiles only (list/timeline must not breathe).
  // Grayscale reinforces the always-visible Broken/Dead badge; terminal-bad
  // statuses and the standalone broken/dead flags all qualify.
  const failed = viewMode === 'tiles'
    && (isFailedStatus(bookmark.status) || bookmark.is_broken_link === 1 || bookmark.is_dead === 1);
  const pending = viewMode === 'tiles' && !failed && isPendingStatus(bookmark.status);

  // Tiles surface badges as a corner overlay over the media frame: serious
  // badges (Broken / Dead / low-confidence / Failed) at rest, the rest on hover.
  // List and timeline render the full set inline, so they don't use this. The
  // overlay is decorative (aria-hidden); the serious labels reach assistive tech
  // through the title button's accessible name (below), so a screen-reader user
  // on tiles doesn't switch views to learn a link is broken/dead/failed/low-confidence.
  const tileBadges = isTiles ? computeBadges(bookmark) : [];
  const tileSeriousLabels = tileBadges.filter((b) => b.serious).map((b) => b.label);

  const className = [
    styles.card,
    styles[viewMode],
    selected ? styles.selected : '',
    justAdded ? styles.justAdded : '',
    selectionMode ? styles.selectable : '',
    pending ? styles.pending : '',
    failed ? styles.failed : '',
  ].filter(Boolean).join(' ');

  // The whole-card click/drag/long-press is a mouse convenience that lives on
  // the root, but every view exposes its accessible "open" action on an inner
  // title <button>. The root therefore carries no button role or tabIndex in
  // any view — that also stops its child controls (carousel arrows, ★, {},
  // category chip) from being nested-interactive.
  // Exactly one owner per click: the root attaches handlers only when it owns
  // the click (tiles/timeline pass onClick). In list view BookmarkList's row
  // wrapper owns click/aux-click/long-press — attaching here too would double-
  // fire the functional Set toggle and cancel every selection click.
  const interactive = !!onClick;

  const categoryLeaf = bookmark.category_path && bookmark.category_path !== 'Uncategorized'
    ? splitCategoryPath(bookmark.category_path)
    : null;

  // The carousel is live only when it has more than one panel — drives the
  // swipe handlers, the dots/arrows, and the group semantics on the frame.
  const hasCarousel = tileViews.length > 1;

  return (
    <div
      className={className}
      draggable
      onDragStart={(e) => {
        const ids = dragIds && dragIds.length > 0 ? dragIds : [bookmark.id];
        e.dataTransfer.setData('text/x-bookmark-ids', JSON.stringify(ids));
        e.dataTransfer.effectAllowed = 'move';
        if (ids.length > 1) {
          const ghost = document.createElement('div');
          ghost.textContent = `${ids.length} bookmarks`;
          ghost.style.cssText = 'position:fixed;left:-9999px;padding:6px 14px;background:var(--accent-solid);color:var(--accent-solid-text);border-radius:12px;font-size:13px;font-weight:600;white-space:nowrap;';
          document.body.appendChild(ghost);
          e.dataTransfer.setDragImage(ghost, 0, 0);
          requestAnimationFrame(() => ghost.remove());
        }
      }}
      onClick={interactive ? (e) => handleClick(e) : undefined}
      onAuxClick={interactive ? handleAuxClick : undefined}
      onPointerDown={interactive ? handlePointerDown : undefined}
      onPointerUp={interactive ? cancelLongPress : undefined}
      onPointerLeave={interactive ? cancelLongPress : undefined}
      onPointerCancel={interactive ? cancelLongPress : undefined}
      onContextMenu={(e) => { cancelLongPress(); onContextMenu?.(e, bookmark); }}
    >
      {selectionMode ? (
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={selected}
          aria-label={`Select ${displayTitle || formatUrl(bookmark.url)}`}
          onChange={() => onToggleSelect?.(bookmark.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className={styles.dragGrip} aria-hidden="true">&#8942;</span>
      )}

      {viewMode === 'tiles' ? (
        <>
          <div
            className={styles.mediaFrame}
            onPointerDown={hasCarousel ? onMediaPointerDown : undefined}
            onPointerMove={hasCarousel ? onMediaPointerMove : undefined}
            onPointerUp={hasCarousel ? onMediaPointerUp : undefined}
            role={hasCarousel ? 'group' : undefined}
            aria-label={hasCarousel ? displayTitle || formatUrl(bookmark.url) : undefined}
          >
            {previewKind && !previewError && !previewFailed ? (
              // Image panel. While the next asset loads, hold a neutral frame
              // (the mediaFrame bg) and crossfade the image in — never flash the
              // favicon placeholder mid-swap.
              previewSrc ? (
                <img
                  ref={(el) => { if (el?.complete) { if (previewPath) rememberLoadedAsset(previewPath); setImgLoaded(true); } }}
                  className={`${styles.media} ${previewKind === 'screenshot' ? styles.mediaScreenshot : ''} ${imgLoaded || previewCached ? styles.loaded : ''}`}
                  src={previewSrc}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onLoad={() => { if (previewPath) rememberLoadedAsset(previewPath); setImgLoaded(true); }}
                  onError={() => setPreviewError(true)}
                />
              ) : (
                <div className={styles.mediaLoading} />
              )
            ) : faviconSrc && !faviconError ? (
              <div className={styles.mediaPlaceholder}>
                <img src={faviconSrc} alt="" width={48} height={48} onError={() => setFaviconError(true)} />
              </div>
            ) : (
              <div className={styles.mediaPlaceholder}>
                <span className={styles.tilePlaceholderIcon}>
                  {initialFor(bookmark)}
                </span>
              </div>
            )}

            {tileBadges.length > 0 && (
              <div className={styles.tileBadges} aria-hidden="true">
                {tileBadges.map((b) => (
                  <span
                    key={b.label}
                    className={`${styles.tileBadge} ${TILE_BADGE_VARIANT[b.variant] ?? styles.tbNeutral} ${b.serious ? '' : styles.tileBadgeHover}`}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            )}

            {/* Carousel controls — arrows mid-sides, dots bottom-centre; the
              * badge overlay holds the top-left corner, so the three zones never
              * collide. Arrows are mouse/touch-only (tabIndex -1); the keyboard
              * path is the [ ] keys on the focused title. */}
            {tileViews.length > 1 && (
              <>
                <button
                  type="button"
                  tabIndex={-1}
                  className={`${styles.navArrow} ${styles.navPrev}`}
                  aria-label="Previous view"
                  onClick={(e) => { e.stopPropagation(); goView(activeIndex - 1); }}
                >
                  &#8249;
                </button>
                <button
                  type="button"
                  tabIndex={-1}
                  className={`${styles.navArrow} ${styles.navNext}`}
                  aria-label="Next view"
                  onClick={(e) => { e.stopPropagation(); goView(activeIndex + 1); }}
                >
                  &#8250;
                </button>
                <div className={styles.dots} aria-hidden="true">
                  {tileViews.map((v, i) => (
                    <span key={v} className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ''}`} />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className={styles.content}>
            <button
              type="button"
              className={styles.tileTitle}
              aria-label={
                tileSeriousLabels.length
                  ? `Open ${displayTitle || formatUrl(bookmark.url)} (${tileSeriousLabels.join(', ')})`
                  : `Open ${displayTitle || formatUrl(bookmark.url)}`
              }
              onAuxClick={(e) => {
                // Middle-click opens the URL. The root's handleAuxClick skips
                // button targets, so the title carries its own (it's the area
                // users aim at for "open in new tab").
                if (e.button === 1 && onOpenUrl) { e.preventDefault(); onOpenUrl(bookmark); }
              }}
              onKeyDown={(e) => {
                // Bracket / comma-period switch carousel panels while the card's
                // open-button has focus (the card root is no longer focusable).
                if (tileViews.length > 1 && (e.key === '[' || e.key === ']' || e.key === ',' || e.key === '.')) {
                  e.preventDefault();
                  goView(activeIndex + (e.key === ']' || e.key === '.' ? 1 : -1));
                }
                // Enter / Space fall through to the native button click, which
                // bubbles to the card root's onClick (opens the preview).
              }}
            >
              <Highlight text={displayTitle || formatUrl(bookmark.url)} query={searchQuery} />
            </button>
            {displaySummary && displaySummary.trim() !== (displayTitle || '').trim() && (
              <p className={styles.summary}>
                <Highlight text={displaySummary} query={searchQuery} />
              </p>
            )}
            <div className={styles.source}>
              <span className={styles.sourceFavicon}>
                {faviconSrc && !faviconError ? (
                  <img src={faviconSrc} alt="" width={16} height={16} onError={() => setFaviconError(true)} />
                ) : (
                  <span className={styles.sourceFaviconInitial}>
                    {initialFor(bookmark)}
                  </span>
                )}
              </span>
              <span className={styles.url}>
                <Highlight text={formatUrl(bookmark.url)} query={searchQuery} />
              </span>
              {categoryLeaf && (
                <button
                  type="button"
                  className={styles.sourceCat}
                  onClick={(e) => { e.stopPropagation(); onSelectCategory?.(bookmark.category_path!); }}
                  aria-label={`Filter by ${categoryLeaf[categoryLeaf.length - 1]}`}
                >
                  <TagIcon size={11} className={styles.sourceCatIcon} />
                  <span className={styles.sourceCatLabel}>{categoryLeaf[categoryLeaf.length - 1]}</span>
                </button>
              )}
              <span className={styles.hoverActions}>
                {bookmark.has_translations === 1 && (
                  // Discoverability hint, not a control: translations + the full
                  // summary live one click away in the preview drawer.
                  <span
                    className={`${styles.langChip} ${styles.langMore}`}
                    title="Translations available — switch language in the preview"
                  >
                    <TranslateIcon size={10} />
                  </span>
                )}
                {langInfo?.isFallback && (
                  <span
                    className={`${styles.langChip} ${styles.langFallback}`}
                    title={`Showing original — ${langInfo.label} not available`}
                  >
                    {langInfo.label}
                  </span>
                )}
                <BookmarkMetadataActions
                  bookmark={bookmark}
                  showMetaButton={showMetaButton}
                  onShowMeta={onShowMeta}
                  onToggleFavourite={onToggleFavourite}
                />
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.thumb}>
            {previewSrc && !previewError ? (
              <img
                className={styles.thumbImg}
                src={previewSrc}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setPreviewError(true)}
              />
            ) : faviconSrc && !faviconError ? (
              <img
                className={styles.thumbFavicon}
                src={faviconSrc}
                alt=""
                width={20}
                height={20}
                loading="lazy"
                decoding="async"
                onError={() => setFaviconError(true)}
              />
            ) : (
              <span className={styles.thumbInitial}>
                {initialFor(bookmark)}
              </span>
            )}
          </div>

          <div className={styles.rowMain}>
            <button
              type="button"
              className={styles.rowTitle}
              aria-label={`Open ${displayTitle || formatUrl(bookmark.url)}`}
              onAuxClick={(e) => {
                // Middle-click opens the URL — the root's handleAuxClick skips
                // button targets, so the title carries its own.
                if (e.button === 1 && onOpenUrl) { e.preventDefault(); onOpenUrl(bookmark); }
              }}
            >
              <Highlight text={displayTitle || formatUrl(bookmark.url)} query={searchQuery} />
            </button>
            {displaySummary && (
              <span className={styles.rowSummary}>
                <Highlight text={displaySummary} query={searchQuery} />
              </span>
            )}
          </div>

          <div className={styles.rowTrailing}>
            <div className={styles.rowTrailingTop}>
              {categoryLeaf && (
                <CategoryPills
                  categoryPath={bookmark.category_path!}
                  mode={categoryDisplayMode}
                  onSelect={onSelectCategory}
                />
              )}
              <span className={styles.date}>{displayDate}</span>
            </div>
            <div className={styles.rowMeta}>
              <BookmarkBadges bookmark={bookmark} />
              {langInfo && (
                <span
                  className={`${styles.langChip} ${langInfo.isFallback ? styles.langFallback : ''}`}
                  title={langInfo.isFallback ? `Showing original — ${langInfo.label} not available` : `Showing ${langInfo.label}`}
                >
                  {langInfo.label}
                </span>
              )}
              <BookmarkMetadataActions
                bookmark={bookmark}
                showMetaButton={showMetaButton}
                onShowMeta={onShowMeta}
                onToggleFavourite={onToggleFavourite}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Reference-compare bookmark (TanStack Query rebuilds the object on patches);
// shallow-compare other props to avoid the field-whitelist drift footgun.
export const BookmarkCard = memo(BookmarkCardImpl);

function BookmarkMetadataActions({
  bookmark,
  showMetaButton,
  onShowMeta,
  onToggleFavourite,
}: Pick<BookmarkCardProps, 'bookmark' | 'showMetaButton' | 'onShowMeta' | 'onToggleFavourite'>) {
  const favourite = bookmark.is_favourite === 1;
  return (
    <>
      {showMetaButton && (
        <button
          type="button"
          className={styles.metaBtn}
          onClick={(event) => { event.stopPropagation(); onShowMeta?.(bookmark); }}
          aria-label="Show metadata"
        >
          {'{}'}
        </button>
      )}
      {onToggleFavourite && (
        <button
          type="button"
          className={`${styles.favBtn} ${favourite ? styles.favActive : ''}`}
          onClick={(event) => { event.stopPropagation(); onToggleFavourite(bookmark.id); }}
          aria-label={favourite ? 'Remove from favourites' : 'Add to favourites'}
        >
          {favourite ? '★' : '☆'}
        </button>
      )}
    </>
  );
}

/** The carousel panels a tile should cycle, in fixed order, filtered to those
 *  with content. A single image collapses to just the hero — an OG/Screenshot-
 *  only panel would duplicate it. */
function buildTileViews(
  bookmark: BookmarkListItem,
  heroImageSource: HeroImageSource,
): TileView[] {
  // The hero already shows the preferred image — only add a panel for the
  // *other* image, so cycling never lands on two identical frames.
  const heroKind = pickPreviewKind(bookmark, heroImageSource);
  const views: TileView[] = ['hero'];
  if (bookmark.has_og_image === 1 && heroKind !== 'og_image') views.push('og');
  if (bookmark.has_screenshot === 1 && heroKind !== 'screenshot') views.push('screenshot');
  return views;
}

/** Which stored asset a panel renders (null = no image, e.g. a favicon-only hero). */
function assetKindForView(
  view: TileView,
  bookmark: BookmarkListItem,
  heroImageSource: HeroImageSource,
): 'screenshot' | 'og_image' | null {
  if (view === 'og') return 'og_image';
  if (view === 'screenshot') return 'screenshot';
  if (view === 'hero') return pickPreviewKind(bookmark, heroImageSource);
  return null;
}

/** Which image leads the card, honouring the user's source preference
 *  (null = no image → favicon / initial fallback). */
function pickPreviewKind(
  bookmark: BookmarkListItem,
  priority: HeroImageSource,
): 'screenshot' | 'og_image' | null {
  const hasShot = bookmark.has_screenshot === 1;
  const hasOg = bookmark.has_og_image === 1;
  const order: Array<'screenshot' | 'og_image'> =
    priority === 'screenshot' ? ['screenshot', 'og_image'] : ['og_image', 'screenshot'];
  for (const kind of order) {
    if (kind === 'screenshot' && hasShot) return 'screenshot';
    if (kind === 'og_image' && hasOg) return 'og_image';
  }
  return null;
}

/* Category pill sub-component */

function CategoryPills({ categoryPath, mode, onSelect }: {
  categoryPath: string;
  mode: CategoryDisplayMode;
  onSelect?: (path: string) => void;
}) {
  const segments = splitCategoryPath(categoryPath);

  if (mode === 'last') {
    return (
      <button
        type="button"
        className={styles.catBubble}
        onClick={(e) => { e.stopPropagation(); onSelect?.(categoryPath); }}
        aria-label={`Filter by ${segments[segments.length - 1]}`}
      >
        {segments[segments.length - 1]}
      </button>
    );
  }

  // Full mode: clickable pill per path segment.
  return (
    <span className={styles.catBubbles}>
      {segments.map((seg, i) => {
        const partialPath = joinCategoryPath(segments.slice(0, i + 1));
        return (
          <span key={partialPath}>
            {i > 0 && <span className={styles.catSep}>&rsaquo;</span>}
            <button
              type="button"
              className={styles.catBubble}
              onClick={(e) => { e.stopPropagation(); onSelect?.(partialPath); }}
              aria-label={`Filter by ${seg}`}
            >
              {seg}
            </button>
          </span>
        );
      })}
    </span>
  );
}

/** Single-letter placeholder when no preview/favicon is available: the domain's
 *  first letter, falling back to the URL host when the domain is empty. */
function initialFor(bookmark: BookmarkListItem): string {
  const source = bookmark.domain || hostFromUrl(bookmark.url);
  return source ? source[0].toUpperCase() : '?';
}

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/* Fallback-chip label — the real language name from the host's configured
 * options (e.g. "German"); slot-number fallback only when the option list
 * doesn't carry the slot. */

function languageLabel(lang: DisplayLanguage, languageOptions?: LanguageOption[]): string {
  return languageOptions?.find((o) => o.value === lang)?.label
    ?? `Language ${lang.replace('lang', '')}`;
}

/* Search highlight sub-component */

function Highlight({ text, query }: { text: string; query?: string }) {
  if (!query || query.length < 2) return <>{text}</>;

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));

  if (parts.length === 1) return <>{text}</>;

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className={styles.highlight}>{part}</mark>
          : part
      )}
    </>
  );
}
